import { MARKETING_COST_DRIVER_UNIT, type MarketingCostHead } from '@utazzo/shared';
import { UzButton, UzConfirmDialog, UzPageHeader, UzStatusBadge } from '@utazzo/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { CostHeadFormDrawer } from '../components/CostHeadFormDrawer';
import { useDeleteCostHead, useMarketingCostHeads } from '../use-marketing-reports';

const modeLabel = (head: MarketingCostHead): string => {
  if (head.mode === 'CARRY_FORWARD') return 'Once, carried forward';
  if (head.mode === 'PER_MONTH') return 'Per month';
  return 'Calculated';
};

const driverLabel = (head: MarketingCostHead): string => {
  if (head.mode !== 'DERIVED' || !head.driver) return '-';
  const unit = MARKETING_COST_DRIVER_UNIT[head.driver];
  return `₹${head.rate ?? 0} × ${unit}`;
};

const TH =
  'bg-muted text-muted-foreground border-border border-b px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase';
const TD = 'border-border/60 border-b px-3 py-2 align-middle text-sm';

/**
 * Marketing Cost Heads master - the rows of the Spend section on the
 * Marketing report. Deliberately off the report itself: a head is set up once
 * (what it is, how its figure is found, which channel it belongs to) and then
 * the monthly figures are typed on the sheet. An operator working the report
 * never needs this page.
 */
export const MarketingCostHeadsPage = (): React.JSX.Element => {
  const heads = useMarketingCostHeads();
  const remove = useDeleteCostHead();
  const [drawer, setDrawer] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    head: MarketingCostHead | null;
  }>({ open: false, mode: 'create', head: null });

  const rows = heads.data?.data ?? [];

  return (
    <div className="space-y-4">
      <UzPageHeader
        title="Marketing Cost Heads"
        description="Every cost the Marketing report can put against a month, other than ad spend (which is entered per adset). Each head is one Spend row on the sheet."
        actions={
          <UzButton onClick={() => setDrawer({ open: true, mode: 'create', head: null })}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add cost head
          </UzButton>
        }
      />

      <div className="border-border bg-card overflow-x-auto rounded-xl border shadow-sm">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={TH}>Name</th>
              <th className={TH}>How it is found</th>
              <th className={TH}>Rate</th>
              <th className={TH}>Channel</th>
              <th className={TH}>Required</th>
              <th className={TH}>Status</th>
              <th className={`${TH} text-right`}>Sort</th>
              <th className={`${TH} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {heads.isLoading ? (
              <tr>
                <td colSpan={8} className="text-muted-foreground px-3 py-8 text-center text-sm">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-muted-foreground px-3 py-8 text-center text-sm">
                  No cost heads yet. Add one to give the report a Spend row.
                </td>
              </tr>
            ) : (
              rows.map((head) => (
                <tr key={head.id} className="hover:bg-muted/40">
                  <td className={TD}>
                    <div className="flex flex-col">
                      <span className="text-foreground font-medium">{head.name}</span>
                      <span className="text-muted-foreground text-xs">{head.code}</span>
                    </div>
                  </td>
                  <td className={TD}>{modeLabel(head)}</td>
                  <td className={`${TD} tabular-nums`}>{driverLabel(head)}</td>
                  <td className={TD}>{head.querySourceName ?? 'All channels'}</td>
                  <td className={TD}>{head.isRequired ? 'Yes' : 'No'}</td>
                  <td className={TD}>
                    <UzStatusBadge status={head.isActive ? 'active' : 'inactive'} />
                  </td>
                  <td className={`${TD} text-right tabular-nums`}>{head.sortOrder}</td>
                  <td className={`${TD} text-right`}>
                    <div className="flex justify-end gap-1">
                      <UzButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setDrawer({ open: true, mode: 'edit', head })}
                        aria-label={`Edit ${head.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </UzButton>
                      <UzConfirmDialog
                        trigger={
                          <UzButton
                            variant="ghost"
                            size="sm"
                            aria-label={`Delete ${head.name}`}
                            disabled={remove.isPending}
                          >
                            <Trash2 className="text-destructive h-4 w-4" />
                          </UzButton>
                        }
                        title={`Delete ${head.name}?`}
                        description="Refused while the head still has monthly figures - deactivate it instead, or clear those months on the sheet first."
                        confirmLabel="Delete"
                        tone="destructive"
                        onConfirm={async () => {
                          await remove.mutateAsync(head.id);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CostHeadFormDrawer
        open={drawer.open}
        onOpenChange={(open) => setDrawer((prev) => ({ ...prev, open }))}
        mode={drawer.mode}
        head={drawer.head}
      />
    </div>
  );
};
